import React from "react";
import { config, configWithUserFileToken, configWithUserToken, configWithWebToken } from "../services/config";
import { IUserLogin } from "../models/IUserLogin";
import { ILoginInfo } from "../models/ILoginInfo";
import { IRole, IRoles, IUserRole, IUserRoles } from "../models/IUserRoles";
import { ICustomerUser } from "../models/ICustomerUser";
import { hasMultipleRestaurants } from "../utils/activeRestaurant";

export const ICustomerLogin = (username: string, password: string) => {
  const sendObj = {
    username: username,
    password: password,
  };

  return config.post("Auth/UserLogin", sendObj);
};

export const getTokenForWeb = () => {
  const sendObj = {
    email: process.env.REACT_APP_WEB_AUTH_EMAIL ?? "",
    password: process.env.REACT_APP_WEB_AUTH_PASSWORD ?? "",
    deviceId: "",
  };

  return config.post<ILoginInfo>("Auth/Token", sendObj);
};

export const userLoginOps = (userdata: IUserLogin) => {
  return configWithWebToken.post("Auth/AdminLogin", userdata);
};

export interface ILiveChatAgentLoginResponse {
  token: string;
  expiration: string;
  secretKey: string;
  userId: string;
  refreshToken: string;
  refreshTokenExpiration: string;
}

export const liveChatAgentLogin = (userdata: IUserLogin) => {
  return configWithWebToken.post<ILiveChatAgentLoginResponse>("Auth/LiveChatAgentLogin", userdata);
};

export interface IDriverLoginResponse {
  token: string;
  expiration: string;
  secretKey: string;
  userId: string;
  refreshToken: string;
  refreshTokenExpiration: string;
}

export const driverLoginOps = (userdata: IUserLogin) => {
  return configWithWebToken.post<IDriverLoginResponse>("Auth/DriverLogin", userdata);
};

/** Garson girişi yanıtı: kurye/temsilci ile aynı, tek fark `restourantId`'nin dolu gelmesi. */
export interface IWaiterLoginResponse extends IDriverLoginResponse {
  restourantId: string;
}

/**
 * Garson girişi — RestaurantWaiter rolü ve etkin restoran bağı doğrulanır.
 * Hatalar: 404 WaiterRoleNotFound, 403 WaiterRestaurantLinkMissing.
 */
export const waiterLoginOps = (userdata: IUserLogin) => {
  return configWithWebToken.post<IWaiterLoginResponse>("Auth/WaiterLogin", userdata);
};

export const getUserInfo = (userId: string) => {
  return config.post("Auth/AdminLogin", userId);
};

export const getRoles=()=>{
  
  return configWithUserFileToken.get<IRoles>("Role/roleList")
}

/** Rol listesi. Uç düz dizi döner (IRoles tipi eski ve yanlış: { roleList } sarmalayıcısı yok). */
export const getRoleList = () =>
  configWithUserToken.get<IRole[]>("Role/roleList");

export interface ICreateRole {
  roleName: string;        // zorunlu, en çok 100
  roleDescription?: string; // en çok 500
  roleIsEnabled: boolean;
}

/** Yeni rol. ⚠️ Backend aynı isimde rol var mı denetlemiyor; çift kaydı arayüz engellemeli. */
export const createRole = (data: ICreateRole) =>
  configWithUserToken.post<IRole>("Role", data);

/**
 * Kullanıcıya rol atar. ⚠️ Kullanıcıda bu rol zaten varsa (pasif olsa bile) backend reddeder;
 * pasif rol `setUserRoleStatus` ile yeniden açılır.
 */
export const assignUserRole = (customerId: string, roleId: string) =>
  configWithUserToken.post<IUserRole>("UserRole", { customerId, roleId, userRoleIsEnabled: true });

/** Kullanıcı-rol bağını aktif/pasif yapar (silme ucu yok; kaldırmak pasifleştirmektir). */
export const setUserRoleStatus = (userRoleId: string, isEnabled: boolean) =>
  configWithUserToken.put<IUserRole>(`UserRole/SetUserRoleStatus/${userRoleId}?isEnabled=${isEnabled}`);

export const getUserRoles=(userId:string)=>{
  return configWithUserFileToken.get<IUserRoles>("UserRole/RolesByUserId/"+userId);
}

// Token'daki oturuma göre (userId parametresi gerekmeden) giriş yapan kullanıcının rollerini döner.
export const getUserRolesByToken = () => {
  return configWithUserFileToken.get<IUserRoles>("UserRole/GetUserRoles");
}

export const getUserListByRoleIds = (roleIds: string[]) => {
  const params = new URLSearchParams();
  roleIds.forEach(id => params.append("roleIds", id));
  return configWithUserFileToken.get<ICustomerUser[]>(`Auth/UserUserListByRoleIds?${params.toString()}`);
}

export interface IRegisterRestaurantUser {
  customerName: string;
  customerSurName: string;
  customerUserName: string;
  customerTelNo: string;
  customerEmail: string;
  customerBirthDate: string;
  customerIsEnabled: boolean;
  customerLogin: { customerPassword: string };
}

export const registerRestaurantUser = (roleId: string, data: IRegisterRestaurantUser) => {
  return configWithUserToken.post<ICustomerUser>(
    `Auth/RegisterRestaurantUser?roleIds=${roleId}`,
    data
  );
};

export interface IRegisterLiveChatAgent {
  customerName: string;
  customerSurName: string;
  customerUserName: string;
  customerTelNo: string;
  customerEmail: string;
  customerBirthDate: string;
  customerIsEnabled: boolean;
  customerLogin: { customerPassword: string };
}

export const registerLiveChatAgent = (data: IRegisterLiveChatAgent) => {
  return configWithUserToken.post<ICustomerUser>("Auth/RegisterLiveChatAgent", data);
};

export interface IRegisterWaiterUser {
  customerName: string;
  customerSurName: string;
  customerUserName?: string;
  customerTelNo: string;
  customerEmail: string;
  /** 13 yaşından büyük olmalı (AgeRestriction) */
  customerBirthDate: string;
  customerIsEnabled: boolean;
  /** Parola en az 6, en çok 128 karakter */
  customerLogin: { customerPassword: string };
}

/**
 * Garson hesabı açar. Restoran **istekten değil, çağıranın token'ından** çözülür:
 * tam olarak bir erişilebilir restoranı olan RestourantAdmin kullanabilir.
 */
export const registerWaiterUser = (data: IRegisterWaiterUser, restaurantId?: string) => {
  // Çok restoranlı yöneticide hangi restorana açılacağı söylenmeli; yoksa 400 WaiterRestaurantAmbiguous.
  // Tek restoranlıda parametre gönderilmez (eski davranış).
  const query = restaurantId && hasMultipleRestaurants() ? `?restaurantId=${encodeURIComponent(restaurantId)}` : "";
  return configWithUserToken.post<ICustomerUser>(`Auth/RegisterWaiterUser${query}`, data);
};

export const postRestaurantUser = (customerId: string, restourantId: string) => {
  return configWithUserToken.post("RestourantUser", {
    restourantUserIsEnabled: true,
    customerId,
    restourantId,
  });
};

export const removeUser = (userId: string) =>
  configWithUserToken.delete(`Auth/RemoveUser/${userId}`);

export const getCustomerDetails = (userId: string) =>
  configWithUserToken.get(`Auth/CustomerDetails/${userId}`);

export const getCustomerDetailsByToken = () =>
  configWithUserToken.get<ILoginInfo>("Auth/CustomerDetailsByToken");
